import React, { useMemo, useEffect } from "react";
// React Router hooks:
//  - useParams: get the :series segment from the URL (/mini-series/:series)
//  - useLocation: receive Link state (we expect fiction.js to pass state.series)
//  - useNavigate: programmatic navigation to book page on click
import { useParams, useLocation, useNavigate } from "react-router-dom";

import parse from "html-react-parser";

// Import only the KotR pieces we need (we must NOT change book contents)
import { KotRSeries, KotRBooks } from "../Components/Books";

// Reuse your site CSS so the grid uses the same styles as fiction page
import "../CSS_stuff/format_fiction.css";

/*
  normalize()
  - Turn strings into a canonical lower-case, alphanumeric-with-spaces form
  - Removes smart quotes and non-alphanumerics so comparisons tolerate punctuation/encoding.
  - Used to match titles/slugs/miniSeries values robustly.
*/
const normalize = (s) =>
  String(s || "")
    .toLowerCase()
    .replace(/[\u2018\u2019\u201C\u201D]/g, "") // remove smart quotes
    .replace(/[^a-z0-9]+/g, " ")             // replace non-alphanumeric with single space
    .trim();

export default function MiniSeriesPage() {
  // Read :series from URL and decode it (Link uses encodeURIComponent)
  const { series } = useParams();
  const seriesKey = series ? decodeURIComponent(series) : null;

  // Read location.state which fiction.js should set: state.series = ms (mini-series object)
  const location = useLocation();

  // ===== REPLACE THE OLD single-line seriesFromState WITH THIS BLOCK =====
  const seriesFromState = (() => {
    const st = location && location.state;
    // eslint-disable-next-line no-console
    console.log("[MiniSeries] location.state:", st, " params.series:", series);

    if (!st) return null;
    if (st.series && typeof st.series === "object") return st.series;
    if (typeof st === "object" && st.title) return st;
    return null;
  })();

  // <<< ADD THESE TWO LINES TO FIX the 'seriesEntry' and 'navigate' undefined errors >>>
  const navigate = useNavigate();

  const seriesEntry = useMemo(() => {
    // prefer the passed-in state object
    if (seriesFromState) return seriesFromState;
    if (!seriesKey) return null;
    const key = normalize(seriesKey);
    if (!Array.isArray(KotRSeries)) return null;
    return KotRSeries.find((ms) => {
      if (!ms || !ms.title) return false;
      const t = normalize(ms.title);
      return t === key || t.includes(key) || key.includes(t);
    }) || null;
  }, [seriesFromState, seriesKey]);


  // <<< ADD THIS block to define `books` (was missing) >>>
  const books = useMemo(() => {
    // If the mini-series entry provides an explicit books[] use it (preserves order/content).
    if (seriesEntry && Array.isArray(seriesEntry.books) && seriesEntry.books.length > 0) {
      return seriesEntry.books;
    }

    // No explicit entry — if no seriesKey, return empty
    if (!seriesKey) return [];

    const key = normalize(seriesEntry?.title || seriesKey);

    // Filter KotRBooks by miniSeries metadata (string or object)
    return KotRBooks.filter((b) => {
      if (!b) return false;
      const ms = b.miniSeries;
      if (!ms) return false;

      if (typeof ms === "string") {
        const n = normalize(ms);
        return n === key || n.includes(key) || key.includes(n);
      }

      if (typeof ms === "object") {
        if (ms.title && normalize(ms.title) === key) return true;
        if (ms.name && normalize(ms.name) === key) return true;
        if (ms.slug && normalize(ms.slug) === key) return true;
        // partial-match tolerances
        if (ms.title && normalize(ms.title).includes(key)) return true;
        if (ms.name && normalize(ms.name).includes(key)) return true;
      }

      return false;
    });
  }, [seriesEntry, seriesKey]);


  // 3) Determine a human-friendly display title:
  //    Prefer seriesEntry.title, else derive from first matched book's miniSeries metadata, else fallback to URL.
  const displayTitle = useMemo(() => {
    if (seriesEntry && seriesEntry.title) return seriesEntry.title;
    if (books && books.length > 0) {
      const ms = books[0].miniSeries;
      return typeof ms === "string" ? ms : (ms && (ms.title || ms.name)) || seriesKey;
    }
    return seriesKey || "Mini-series";
  }, [seriesEntry, books, seriesKey]);

  // Helper to navigate to book page; preserve your existing BookPage behavior by passing the book object in state
  function handleBookClick(book) {
    if (!book || !book.link) return;
    navigate(`/books/${book.link}`, { state: { book } });
  }


  useEffect(() => {
    // smooth scroll when navigating via links; change to behavior: "auto" if you prefer instant jump
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  }, [seriesKey, seriesEntry]);

  // ---------- Render ----------
  // This layout intentionally matches the display used in fiction.js:
  //  - banner/description (if present on the mini-series object)
  //  - a book grid using the same .book-grid and .book-item classes so your CSS applies unchanged
  return (
    <div className="mini-series-page">

      {/* Banner image (if the series object provides one). We do NOT add or change content. */}
      {seriesEntry && seriesEntry.image && (
        <div className="mini-series-banner" style={{ marginBottom: 12 }}>
          <img
            src={seriesEntry.image}
            alt={seriesEntry.title}
            style={{ width: "100%", height: "auto", borderRadius: 6 }}
            loading="lazy"
          />
        </div>
      )}

      {/* Series description (if present) - rendered with HTML parser so your existing HTML markup displays */}
      {seriesEntry && (seriesEntry.description || seriesEntry.Description) && (
        // Some of your objects used 'description' (lowercase) and original book objects used 'Description'.
        // We prefer the seriesEntry.description but also allow seriesEntry.Description for compatibility.
        <div className="series-highlight" style={{ marginBottom: 12 }}>
          <div className="series-description">
            {parse(seriesEntry.description || seriesEntry.Description || "")}
          </div>
        </div>
      )}

      {/* If no books found, show a short message */}
      {books.length === 0 && (
        <p>No books found for this mini-series.</p>
      )}

      {/* Books grid - uses EXACT same classes and click behaviour as fiction page */}
      {books.length > 0 && (
        <section className="book-grid-section" aria-labelledby="books-heading">

          {/* Use same .book-grid CSS as in fiction so display is identical */}
          <div className="book-grid">
            {books.map((b) => (
              <div
                key={b.link}
                className="book-item"
                onClick={() => handleBookClick(b)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleBookClick(b); }}
                style={{ cursor: "pointer" }}
              >
                {/* Keep the image/title HTML exactly as in your Books.js objects */}
                {b.image ? (
                  <img src={b.image} alt={b.title} loading="lazy" />
                ) : (
                  <div className="no-cover">{b.title}</div>
                )}

                {/* Title: keep the original HTML rendering behavior so formatting is unchanged */}
                <p dangerouslySetInnerHTML={{ __html: b.title }} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}